/*
 * Copyright (c) 2025.  Chad and contributors.
 * All rights reserved.
 */

import {computed, watch} from 'vue';
import {query, set} from 'vue-gtag';
import {useCompany} from '@/composables/useCompany';
import type {BookingInterface} from '@/types/booking';
import type {FleetApp} from '@/types';
import {d} from '@/utils/consoleHelper.ts';
import {CENTS_TO_DOLLARS} from '@/constants/money';
import {FLEETWIRE_MEASUREMENT_ID} from '@/plugins/gtag';

export interface TrackingApp {
    app: string;
    enabled: boolean;
    identifier: 'google_analytics' | 'meta_pixel' | string;
    measurement_id?: string; // for GA
    pixel_id?: string; // for Meta, etc.
}

const configuredTenantTags = new Set<string>();

export function useAppTracking() {
    const {company, fleetAppSubscriptions} = useCompany();

    const googleAnalyticsApp = computed<TrackingApp | undefined>(() => {
        const app = fleetAppSubscriptions.value.find((sub) => sub.identifier === 'google_analytics');
        if (!app) {
            return undefined;
        }
        // Check if it's already in TrackingApp format (has 'app' and 'enabled' properties)
        if ('app' in app && 'enabled' in app) {
            return app as unknown as TrackingApp;
        }
        // Otherwise, transform from the FleetApp format
        const fleetApp = app as FleetApp;
        const isActive = typeof fleetApp.active === 'string'
            ? fleetApp.active === '1' || fleetApp.active.toLowerCase() === 'true'
            : Boolean(fleetApp.active);
        return {
            app: fleetApp.name ?? 'Google Analytics',
            enabled: isActive,
            identifier: fleetApp.identifier,
            measurement_id: (fleetApp as any).measurement_id ?? (fleetApp as any).setting_values?.api_key,
        };
    });

    /**
     * Attach the company slug to all subsequent GA hits (Fleetwire's own
     * property registers it as a custom dimension). Waits for the company
     * to load when called before the store is hydrated.
     */
    const syncCompanyTracking = (): void => {
        const slug = company.value?.slug;
        if (slug) {
            set({company_slug: slug});
            return;
        }

        const stop = watch(company, (value) => {
            if (value?.slug) {
                set({company_slug: value.slug});
                stop();
            }
        });
    };

    /**
     * Configure the tenant's own GA tag alongside the Fleetwire tag loaded at
     * boot (plugins/gtag.ts). Must never call vue-gtag configure() here — that
     * would replace the Fleetwire tag instead of adding a second one.
     */
    const initGoogleAnalytics = async (): Promise<void> => {
        d.warn('🌐 Initializing Google Analytics...');
        const {isOnline} = await import('@/utils/network');
        if (!isOnline()) return;

        syncCompanyTracking();

        const ga = googleAnalyticsApp.value;
        if (ga?.enabled && ga.measurement_id) {
            if (!configuredTenantTags.has(ga.measurement_id)) {
                configuredTenantTags.add(ga.measurement_id);
                query('config', ga.measurement_id);
                d.log(`✅ Tenant Google Analytics configured: ${ga.measurement_id}`);
            }
        } else {
            d.warn('⚠️ Tenant Google Analytics not configured');
        }
    };

    const trackEvent = (eventName: string, data: Record<string, any> = {}) => {
        // GA4 drops/skips some hits when params contain undefined — only send defined values.
        const params = Object.fromEntries(
            Object.entries(data).filter(([, value]) => value !== undefined && value !== null),
        );

        // Pin destination — vue-gtag debug otherwise logs "Sending event to undefined".
        const payload = {...params, send_to: FLEETWIRE_MEASUREMENT_ID};

        if (typeof window.gtag === 'function') {
            window.gtag('event', eventName, payload);
            d.log(`📊 Tracked event: ${eventName}`, payload);
            return;
        }

        // Queue until gtag.js finishes loading (same pattern as the official snippet).
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(['event', eventName, payload]);
        d.log(`📊 Queued event (gtag not ready): ${eventName}`, payload);
    };

    const trackPurchase = (booking: BookingInterface) => {
        const trackedKey = `purchase_tracked_${booking.b_id}`;

        if (!sessionStorage.getItem(trackedKey)) {
            const orderLines = booking?.order?.lines || [];
            trackEvent('purchase', {
                transaction_id: booking.b_id,
                currency: 'USD',
                value: booking.total,
                items: orderLines.map((line) => ({
                    item_name: line.title,
                    item_id: line.item_id,
                    price: line.price_in_cents * CENTS_TO_DOLLARS,
                    quantity: line.quantity,
                })),
            });

            sessionStorage.setItem(trackedKey, 'true');
            d.log(`📦 Purchase tracked for booking ${booking.b_id}`);
        } else {
            d.log(`⚠️ Purchase already tracked for booking ${booking.b_id}`);
        }
    };


    return {
        initGoogleAnalytics,
        syncCompanyTracking,
        trackEvent,
        trackPurchase,
        googleAnalyticsApp,
    };
}
